import { Fetcher } from '@this/src/util';
import type ForbiddenRequestFormArgs from '@this/src/domain/organization/forbidden_request_form';
import Organization, {
  convertOrganizationResponseToArgs,
  type OrganizationResponseArgs
} from '@this/src/domain/organization/organization3';
import type { OrganizationBillReceiverArgs } from '@this/src/domain/organization/organization_bill_receiver2';
import OrganizationBillReceiver from '@this/src/domain/organization/organization_bill_receiver2';
import ForbiddenRequestForm from '@this/src/domain/organization/forbidden_request_form';
import FareOption from '@this/src/domain/organization/fare_option';
import type { FareOptionArgs } from '@this/src/domain/organization/fare_option';
import MarginType from '@this/src/domain/organization/margin_type2';
import type { OptionPlanPriceArgs } from '@this/src/domain/plan/option_plan_price';
import OptionPlanPrice from '@this/src/domain/plan/option_plan_price';

interface ShowOrganizationResponse {
  organization: ShowOrganizationResponseArgs;
}

interface ShowOrganizationResponseArgs extends OrganizationResponseArgs {
  forbidden_request_forms?: ForbiddenRequestFormArgs[];
  organization_bill_receivers?: OrganizationBillReceiverArgs[];
  fare_options?: FareOptionArgs[];
  margin_types?: MarginType[];
  current_option_plan_prices?: OptionPlanPriceArgs[];
}

export interface ShowOrganization extends Organization {
  forbiddenRequestForms: ForbiddenRequestForm[];
  organizationBillReceivers: OrganizationBillReceiver[];
  fareOptions: FareOption[];
  marginTypes: MarginType[];
  currentOptionPlanPrices: OptionPlanPrice[];
}

export default class OrganizationAPI {
  static async show(organizationId: number): Promise<ShowOrganization> {
    const res = await Fetcher.get<ShowOrganizationResponse>(`/arrangement/organizations/${organizationId}.json`);
    const responseOrg = res.organization;

    const organization = new Organization(convertOrganizationResponseToArgs(responseOrg));
    return Object.assign(organization, {
      forbiddenRequestForms: (responseOrg.forbidden_request_forms || []).map(f => new ForbiddenRequestForm(f)),
      organizationBillReceivers: (responseOrg.organization_bill_receivers || []).map(
        r => new OrganizationBillReceiver(r)
      ),
      fareOptions: (responseOrg.fare_options || []).map(f => new FareOption(f)),
      marginTypes: this.normalizeMarginTypes(responseOrg.margin_types),
      currentOptionPlanPrices: (responseOrg.current_option_plan_prices || []).map(p => new OptionPlanPrice(p))
    });
  }

  private static normalizeMarginTypes(data?: MarginType[]): MarginType[] {
    const apiMarginTypes = (data || []).map(m => new MarginType(m));

    const getMarginType = (findFn: (m: MarginType) => boolean, defaults: Partial<MarginType>): MarginType => {
      return apiMarginTypes.find(findFn) || new MarginType(defaults);
    };

    return [
      getMarginType(m => m.category === 'domestic_flight', {
        category: 'domestic_flight',
        baseType: 'percentage',
        baseAmount: 5
      }),
      getMarginType(m => m.category === 'domestic_shinkansen', {
        category: 'domestic_shinkansen',
        baseType: 'percentage',
        baseAmount: 5
      }),
      getMarginType(m => m.category === 'domestic_hotel', { category: 'domestic_hotel', baseAmount: 0 }),
      getMarginType(m => m.category === 'domestic_package', { category: 'domestic_package', baseAmount: 0 }),
      getMarginType(m => m.isForeign, { category: 'foreign', baseType: 'fixed', baseAmount: 2200 }),
      getMarginType(m => m.category === 'foreign_hotel', { category: 'foreign_hotel', baseAmount: 0 }),
      getMarginType(m => m.category === 'domestic_rental_car', { category: 'domestic_rental_car', baseAmount: 0 })
    ];
  }
}
